import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import { Context } from "../../../context";
import { getMonthsTexts } from "../../utils";
import { Props } from "./types";
import styles from "./styles.scss";

function isWeekStart(dt: DateTime) {
  return dt.weekday === 1;
}

function startWeek(weeks: DateTime[][], startDate: DateTime) {
  return [...weeks, [startDate]];
}

function isInWeek([firstDt]: DateTime[], dt: DateTime) {
  // Preserve the original mapping: 0=Sun, 1=Mon, ..., 6=Sat for the formula.
  const firstDayIndex = firstDt.weekday === 7 ? 0 : firstDt.weekday;
  const daysToAdd =
    7 - (firstDayIndex === 0 ? 6 : firstDayIndex === 1 ? 0 : firstDayIndex);
  const lastDt = firstDt
    .plus({ days: daysToAdd })
    .set({ hour: 23, minute: 59, second: 59 });
  return dt >= firstDt && dt <= lastDt;
}

function findWeekIndex(weeks: DateTime[][], dt: DateTime) {
  return weeks.findIndex((week) => isInWeek(week, dt));
}

function addToWeek(weeks: DateTime[][], dt: DateTime) {
  const index = findWeekIndex(weeks, dt);

  return index >= 0
    ? [
        ...weeks.slice(0, index),
        [...weeks[index], dt],
        ...weeks.slice(index + 1),
      ]
    : [...weeks, [dt]];
}

function getWeeks(dates: DateTime[]) {
  return dates.reduce<DateTime[][]>(
    (weeks, dt) =>
      isWeekStart(dt) ? startWeek(weeks, dt) : addToWeek(weeks, dt),
    [],
  );
}

// Was a hand-rolled ISO 8601 week-number computation using UTC arithmetic
// to dodge DST. Replaced with Luxon's built-in `weekNumber`, which is the
// same algorithm and is well-defined across DST.
function getWeekIndex(week: DateTime[]): number | null {
  if (week.length === 0) return null;
  return week[0].weekNumber;
}

function Month({ month, useWeeks = true }: Props) {
  const { t } = useTranslation();
  const months = getMonthsTexts(t);
  const firstDt = month[0];
  const { cell } = React.useContext(Context);
  const cellHeight = (cell.height + (cell.height + cell.height * (1 / 3))) / 2;

  return (
    <div className={styles.month}>
      <p className={styles.monthName}>
        {months[firstDt.month - 1]}{" "}
        <span className={styles.year}>{firstDt.year}</span>
      </p>
      {useWeeks && (
        <div className={styles.weeks}>
          {getWeeks(month).map((week, index) => {
            const weekIndex = getWeekIndex(week) || -1;
            const weekFirst = week[0];

            return (
              <div
                key={index}
                className={classNames(styles.week, {
                  [styles.even]: weekIndex % 2 === 0,
                  [styles.odd]: weekIndex % 2 !== 0,
                  [styles.cutOffWeek]: week.length !== 7,
                })}
                style={{
                  width: cell.width * week.length,
                  minHeight: cellHeight,
                  height: 0,
                }}
              >
                {week.length > 3 && (
                  <p
                    style={
                      week.length - 7
                        ? (() => {
                            const isMonday = weekFirst.weekday === 1;
                            return {
                              [isMonday ? "marginRight" : "marginLeft"]:
                                (cell.width / (isMonday ? 1 : 2)) *
                                (week.length / 2) *
                                -1,
                              zIndex: 1,
                            };
                          })()
                        : undefined
                    }
                  >
                    <span>{t("projectPage.ganttPage.week")} </span>
                    <span className={styles.weekNbr}>
                      {weekIndex !== -1 ? weekIndex : ""}
                    </span>
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export { Month };
